import React from 'react';
import SectionLabel from './SectionLabel';
import { ModalType } from '../types';

interface HowToWorkProps {
  onOpenModal: (type: ModalType) => void;
}

const paths = [
  {
    num: '01',
    forWho: 'For brands needing direction first',
    title: 'Clarity &\nAdvisory',
    desc: 'For brands that need diagnosis, direction, and a clear strategic roadmap before committing to execution. We assess your current situation, identify structural weaknesses, and deliver a precise growth blueprint — including positioning, systems, and channel strategy. Ideal as a first engagement before a larger partnership.',
    cta: 'Start With Clarity',
    modal: 'marketing' as ModalType,
  },
  {
    num: '02',
    forWho: 'For brands outside our city',
    title: 'Remote Growth\nPartnership',
    desc: 'For established brands outside Abuja that are ready to grow with full strategic support — without needing us physically present. You capture content internally using our shot direction and production briefs. We handle everything else: editing, messaging, content management, strategy, paid growth, and system architecture.',
    cta: 'Request Proposal',
    modal: 'proposal' as ModalType,
  },
  {
    num: '03',
    forWho: 'For brands ready for full infrastructure',
    title: 'Full Growth\nManagement',
    desc: 'For brands that want HNL fully embedded across strategy, content systems, paid growth infrastructure, and conversion architecture. This is our highest-engagement model — designed for brands that are serious about building a durable, scalable growth system.',
    cta: 'View Packages',
    modal: 'packages' as ModalType,
  },
];

const HowToWork: React.FC<HowToWorkProps> = ({ onOpenModal }) => (
  <section
    id="how-to-work"
    className="relative py-[120px] px-10"
    style={{ background: 'linear-gradient(175deg, #181614 0%, #141210 100%)', borderTop: '1px solid rgba(184,154,90,0.08)' }}
  >
    <div className="max-w-[1200px] mx-auto relative z-10">
      <SectionLabel>How You Can Work With Us</SectionLabel>
      <h2
        style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 'clamp(34px, 4.5vw, 58px)', fontWeight: 300, lineHeight: 1.1, color: '#f2ece0', marginBottom: 8 }}
      >
        Three Ways to
        <br />
        <em style={{ fontStyle: 'italic', color: '#b89a5a' }}>Engage HNL.</em>
      </h2>
      <p style={{ fontSize: 13, fontWeight: 300, color: '#aea89f', maxWidth: 580, lineHeight: 2, letterSpacing: '0.02em', marginBottom: 64 }}>
        This is not a pricing table. It is decision architecture. Choose the path that fits where your brand is and what it needs right now.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: 1, background: 'rgba(184,154,90,0.07)' }}>
        {paths.map((p) => (
          <div
            key={p.num}
            className="fade-up flex flex-col"
            style={{ background: '#181614', padding: '48px 40px', transition: 'background 0.25s' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#1e1a16')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#181614')}
          >
            <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 13, fontWeight: 400, color: '#8a7040', letterSpacing: '0.08em', marginBottom: 28 }}>{p.num}</div>
            <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#b89a5a', marginBottom: 16, fontFamily: 'Montserrat, sans-serif' }}>{p.forWho}</div>
            <h3 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 26, fontWeight: 400, color: '#f2ece0', lineHeight: 1.2, marginBottom: 20 }}>
              {p.title.split('\n').map((line, i) => <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>)}
            </h3>
            <p style={{ fontSize: 12, fontWeight: 300, color: '#78736c', lineHeight: 2, letterSpacing: '0.02em', flex: 1, marginBottom: 36 }}>{p.desc}</p>
            <button
              onClick={() => onOpenModal(p.modal)}
              className="bg-transparent border-none cursor-pointer flex items-center gap-[10px] transition-all duration-200"
              style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#b89a5a', fontFamily: 'Montserrat, sans-serif', padding: 0 }}
              onMouseEnter={(e) => (e.currentTarget.style.gap = '16px')}
              onMouseLeave={(e) => (e.currentTarget.style.gap = '10px')}
            >
              {p.cta} <span style={{ fontSize: 12 }}>→</span>
            </button>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default HowToWork;
